import React, { useEffect, useState } from "react";
import { getGlobalState } from "../../../utils/globalState";
import { getDashboardOverview } from "../../../utils/api";
import Slider from "rc-slider";
import Select from "react-select";
import "rc-slider/assets/index.css";
import "../../../styles/OwnerAdmin.css";
import OverviewUsersChart from "./charts/OverviewUsersChart";
import OverviewInstancePercentAreaChart from "./charts/OverviewInstancePercentAreaChart";

const Overview = ({ expandedSections, toggleSection, runAs, theme }) => {
  const myUserId = getGlobalState().myUserId;
  const runner = runAs === "admin" ? 2 : myUserId;
  const period = runAs === "admin" ? " - Last 90 Days" : "";
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sliderRange, setSliderRange] = useState([0, 0]);

  // Other filters
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [instanceNames, setInstanceNames] = useState([]);
  const [dashboardNames, setDashboardNames] = useState([]);
  // Prepare options
  const instanceOptions = instanceNames.map((name) => ({
    value: name,
    label: name,
  }));
  const [dashboardOptions, setDashboardOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  useEffect(() => {
    const fetchDashboardOverview = async () => {
      setLoadingOverview(true);
      try {
        const data = await getDashboardOverview(runner);
        setOverviewData(data);

        // Extract unique instance_names and dashboard names
        const instances = Array.from(
          new Set(data.map((d) => d.instance_name))
        ).sort();
        // After extracting dashboards as array of { id, name }
        const dashboards = Array.from(
          new Map(
            data.map((d) => [
              d.dashboard_id,
              { id: d.dashboard_id, name: d.dashboard_name },
            ])
          ).values()
        ).sort((a, b) => a.name.localeCompare(b.name));

        setDashboardNames(dashboards.map((d) => `[${d.id}] ${d.name}`));
        setDashboardOptions(
          dashboards.map((d) => ({
            value: d.id,
            label: `[${d.id}] ${d.name}`,
          }))
        );

        setInstanceNames(instances);
        // Extract min and max dates
        const dates = data
          .filter((d) => d.view_date)
          .map((d) => new Date(d.view_date).getTime());
        const min = Math.min(...dates);
        const max = Math.max(...dates);
        setMinDate(new Date(min).toISOString().slice(0, 10));
        setMaxDate(new Date(max).toISOString().slice(0, 10));
        setStartDate(new Date(min).toISOString().slice(0, 10));
        setEndDate(new Date(max).toISOString().slice(0, 10));
        setSliderRange([min, max]);

        const statuses = Array.from(
          new Set(data.map((d) => d.dashboard_status))
        ).sort();
        setStatusOptions(statuses.map((s) => ({ value: s, label: s })));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoadingOverview(false);
    };
    fetchDashboardOverview();
  }, [myUserId, runner]);

  // Handle slider change
  const handleSliderChange = (values) => {
    const [start, end] = values;
    setStartDate(new Date(start).toISOString().slice(0, 10));
    setEndDate(new Date(end).toISOString().slice(0, 10));
  };

  // 4. Update the filter logic to include dashboard_status
  const filteredData = overviewData
    ? overviewData.filter((d) => {
        if (!d.view_date) return false;
        const dDate = d.view_date.slice(0, 10);
        return (
          (!startDate || dDate >= startDate) &&
          (!endDate || dDate <= endDate) &&
          (selectedInstances.length === 0 ||
            selectedInstances.includes(d.instance_name)) &&
          (selectedDashboards.length === 0 ||
            selectedDashboards.includes(d.dashboard_id)) &&
          (selectedStatuses.length === 0 ||
            selectedStatuses.includes(d.dashboard_status))
        );
      })
    : [];

  // Add this after filteredData is defined
  const uniqueDashboardCount = new Set(filteredData.map((d) => d.dashboard_id))
    .size;
  // After uniqueDashboardCount
  const uniqueUserCount = new Set(filteredData.map((d) => d.viewer_id)).size;
  // Count unique published dashboards in filteredData
  const uniquePublishedDashboardCount = new Set(
    filteredData
      .filter((d) => d.dashboard_status === "Published")
      .map((d) => d.dashboard_id)
  ).size;
  // Calculate percentage of published dashboards
  const publishedPercentage =
    uniqueDashboardCount === 0
      ? 0
      : ((uniquePublishedDashboardCount / uniqueDashboardCount) * 100).toFixed(
          1
        );
  // Compute top 10 most viewed dashboards
  const dashboardViewCounts = {};
  filteredData.forEach((d) => {
    if (!dashboardViewCounts[d.dashboard_id]) {
      dashboardViewCounts[d.dashboard_id] = {
        dashboard_id: d.dashboard_id,
        dashboard_name: d.dashboard_name,
        instance_name: d.instance_name,
        dashboard_status: d.dashboard_status,
        count: 0,
      };
    }
    dashboardViewCounts[d.dashboard_id].count += 1;
  });
  const topDashboards = Object.values(dashboardViewCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Compute top 10 most viewing users
  const userViewCounts = {};
  filteredData.forEach((d) => {
    if (!userViewCounts[d.viewer_id]) {
      userViewCounts[d.viewer_id] = {
        viewer_id: d.viewer_id,
        viewer_name: d.viewer_full_name || d.viewer_id,
        count: 0,
      };
    }
    userViewCounts[d.viewer_id].count += 1;
  });
  const topUsers = Object.values(userViewCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return (
    <div className="section">
      {/* commented cause I use new tabs view instead expand view */}
      {/* <div className="section-header" onClick={() => toggleSection('overview')}>
                <h3>Dashboard Usage {period}</h3>
                <i className={`fas fa-chevron-${expandedSections.overview ? 'up' : 'down'}`}></i>
            </div> */}

      {expandedSections.overview && (
        <div className="section-content overview-grid !border-0">
          {loadingOverview && <p>Loading...</p>}
          {!loadingOverview && overviewData && (
            <>
              <div className="space-y-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Filter Dashboard Data
                  </h3>
                  {/* First filter row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Date range slider */}
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date Range: {startDate} to {endDate}
                      </label>
                      <div className="px-2 py-4">
                        <Slider
                          range
                          min={sliderRange[0]}
                          max={sliderRange[1]}
                          value={[
                            new Date(startDate).getTime(),
                            new Date(endDate).getTime(),
                          ]}
                          onChange={handleSliderChange}
                          allowCross={false}
                          tipFormatter={(v) =>
                            new Date(v).toISOString().slice(0, 10)
                          }
                          step={24 * 60 * 60 * 1000}
                          railStyle={{ backgroundColor: "#e5e7eb", height: 4 }}
                          trackStyle={[
                            { backgroundColor: theme.leftNavBackgroundColor, height: 4 },
                          ]}
                          handleStyle={[
                            {
                              borderColor: theme.leftNavBackgroundColor,
                              height: 16,
                              width: 16,
                              marginLeft: -8,
                              marginTop: -6,
                              backgroundColor: "#fff",
                            },
                            {
                              borderColor: theme.leftNavBackgroundColor,
                              height: 16,
                              width: 16,
                              marginLeft: -8,
                              marginTop: -6,
                              backgroundColor: "#fff",
                            },
                          ]}
                        />
                      </div>
                    </div>
                    {/* Instance multi-select */}
                    {/* Instance multi-select */}
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Instances
                      </label>
                      <Select
                        isMulti
                        options={instanceOptions}
                        value={instanceOptions.filter((opt) =>
                          selectedInstances.includes(opt.value)
                        )}
                        onChange={(selected) =>
                          setSelectedInstances(selected.map((opt) => opt.value))
                        }
                        placeholder="Select Instances (Type to search)"
                        className="text-sm"
                        classNamePrefix="select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: "#d1d5db",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#9ca3af",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "#e5e7eb",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "#374151",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "#6b7280",
                            ":hover": {
                              backgroundColor: "#d1d5db",
                              color: "#1f2937",
                            },
                          }),
                        }}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    {/* Dashboard multi-select */}
                    <div className="filter-item">
                      <Select
                        isMulti
                        options={dashboardOptions}
                        value={dashboardOptions.filter((opt) =>
                          selectedDashboards.includes(opt.value)
                        )}
                        onChange={(selected) =>
                          setSelectedDashboards(
                            selected.map((opt) => opt.value)
                          )
                        }
                        placeholder="Select Dashboards (Type to search)"
                      />
                    </div>
                    <div className="filter-item">
                      <Select
                        isMulti
                        options={statusOptions}
                        value={statusOptions.filter((opt) =>
                          selectedStatuses.includes(opt.value)
                        )}
                        onChange={(selected) =>
                          setSelectedStatuses(selected.map((opt) => opt.value))
                        }
                        placeholder="Select Status (Type to search)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Unique Dashboards Card */}
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                  <div className="flex flex-col items-center">
                    <div className=" font-medium text-gray-500 text-center mb-2">
                      Unique Dashboards with Views
                    </div>
                    <div className="text-3xl font-bold" style={{ color: theme.leftNavBackgroundColor}}>
                      {uniqueDashboardCount}
                    </div>
                  </div>
                </div>
                {/* Distinct Users Card */}
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                  <div className="flex flex-col items-center">
                    <div className=" font-medium text-gray-500 text-center mb-2">
                      Distinct Viewing Users
                    </div>
                    <div className="text-3xl font-bold text-sky-500" >
                      {uniqueUserCount}
                    </div>
                  </div>
                </div>
                {/* Published Dashboards Card */}
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                  <div className="flex flex-col items-center">
                    <div className="font-medium text-gray-500 text-center mb-2" >
                      Published Dashboards
                    </div>
                    <div className="text-3xl font-bold " style={{ color: theme.leftNavBackgroundColor}} >
                      {uniquePublishedDashboardCount}
                    </div>
                  </div>
                </div>
                {/* Published Percentage Card */}
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                  <div className="flex flex-col items-center">
                    <div className=" font-medium text-gray-500 text-center mb-2">
                      Published Dashboard Percentage
                    </div>
                    <div className="text-3xl font-bold text-emerald-600">
                      {publishedPercentage}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Top Dashboards Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      Top 10 Most Viewed Dashboards
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Dashboard
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Instance
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Status
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Views
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {topDashboards.map((d, index) => (
                          <tr
                            key={d.dashboard_id}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-6 py-3 whitespace-nowrap font-medium text-gray-900">
                              {d.dashboard_name}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-gray-500">
                              {d.instance_name}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  d.dashboard_status === "Published"
                                    ? "bg-green-100 text-green-800"
                                    : d.dashboard_status === "Pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {d.dashboard_status}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-gray-900 text-right font-semibold">
                              {d.count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Top Users Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      Top 10 Most Viewing Users
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            User Full Name
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Views
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {topUsers.map((u, index) => (
                          <tr
                            key={u.viewer_id}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-6 py-3 whitespace-nowrap  font-medium text-gray-900">
                              {u.viewer_name}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap  text-gray-900 text-right font-semibold">
                              {u.count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                <div className="w-full h-full min-h-[350px]">
                  <OverviewUsersChart data={filteredData} />
                </div>
                <div className="w-full h-full min-h-[350px]">
                  <OverviewInstancePercentAreaChart data={filteredData} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Overview;
